import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { ArrowBack, ErrorOutline } from "@mui/icons-material";
import dayjs from "dayjs";

import api from "services/api";

import { IFetchResponse } from "./CargaOperadora.types";
import { Title } from "./CargaOperadora.styled";
import { CargaOperadoraUploadModal } from "./CargaOperadoraUpload.Modal";
import { CargaOperadoraUploadTable, LinhaComContrato } from "./CargaOperadoraUpload.Table";

type CargaOperadoraProps = {
  organizationId: string;
  /** Sai da Carga operadora e volta para a lista de atividades. */
  onBack: () => void;
};

/** A chave é compartilhada com o modal, que a invalida depois de um upload bem-sucedido. */
export const CARGA_OPERADORA_QUERY_KEY = "carga-operadora";

type MetricProps = {
  label: string;
  value: string | number;
};

function Metric({ label, value }: MetricProps) {
  return (
    <div style={{ width: "100%", border: "solid 1px #E8E8E8", borderRadius: "16px", padding: "16px", display: "flex", flexDirection: "column" }}>
      <p style={{ fontSize: "12px", lineHeight: "20px", color: "#6F6F78", margin: 0 }}>{label}</p>
      <span style={{ fontSize: "20px", lineHeight: "32px", fontWeight: 600, color: "#6F6F78" }}>{value}</span>
    </div>
  );
}

export function CargaOperadora({ organizationId, onBack }: CargaOperadoraProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * O que a tela mostra vem do que está gravado, não do que o upload devolveu. Sem isso um
   * recarregamento zerava a tela, porque o resultado vivia só no estado do componente.
   */
  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: [CARGA_OPERADORA_QUERY_KEY, organizationId],
    queryFn: () => api.get<{ data: IFetchResponse }>(`/v2/carga-operadora/${organizationId}`),
    select: (response) => response.data.data,
  });

  /** Achata as cargas dos vários contratos numa lista só, carregando o contrato em cada linha. */
  const linhas = useMemo<LinhaComContrato[]>(
    () =>
      (data?.cargas ?? []).flatMap((carga) =>
        carga.linhas.map((linha) => ({ ...linha, numeroContrato: carga.numeroContrato }))
      ),
    [data]
  );

  const metrics = useMemo(() => {
    const titulares = linhas.filter(({ isTitular }) => isTitular).length;
    const exclusoes = linhas.filter(({ tipoMovimentacao }) => tipoMovimentacao === "Exclusão").length;

    return [
      { label: "Beneficiários na posição", value: linhas.length },
      { label: "Titulares", value: titulares },
      { label: "Dependentes", value: linhas.length - titulares },
      { label: "Cobertos / Excluídos", value: `${linhas.length - exclusoes} / ${exclusoes}` },
    ];
  }, [linhas]);

  /** A carga mais recente entre os contratos, para o cabeçalho. */
  const ultimaCarga = data?.cargas?.[0];

  function renderBody() {
    if (!isFetched && isFetching) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
          <CircularProgress size={30} thickness={4} />
        </Box>
      );
    }

    if (isError) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" gap={3} textAlign="center">
          <ErrorOutline sx={{ fontSize: 40, color: "error.main" }} />
          <Typography variant="h6" color="textPrimary">Ops! Algo deu errado</Typography>
          <Typography variant="body1" color="textSecondary" maxWidth={400}>
            Não foi possível carregar a posição da operadora no momento. Tente novamente.
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ display: "flex", gap: "40px", marginBottom: "24px" }}>
          <div>
            <Typography sx={{ color: "text.secondary", fontSize: "12px", marginBottom: "8px" }}>Carregado em</Typography>
            <Typography sx={{ color: "text.primary", fontSize: "14px", fontWeight: 700 }}>
              {ultimaCarga ? dayjs(ultimaCarga.carregadoEm).format("DD/MM/YYYY - HH:mm:ss") : "—"}
            </Typography>
          </div>
          <div>
            <Typography sx={{ color: "text.secondary", fontSize: "12px", marginBottom: "8px" }}>Data de referência</Typography>
            <Typography sx={{ color: "text.primary", fontSize: "14px", fontWeight: 700 }}>
              {ultimaCarga?.dataReferencia ? dayjs(ultimaCarga.dataReferencia).format("DD/MM/YYYY") : "—"}
            </Typography>
          </div>
          <div>
            <Typography sx={{ color: "text.secondary", fontSize: "12px", marginBottom: "8px" }}>Contratos com posição</Typography>
            <Typography sx={{ color: "text.primary", fontSize: "14px", fontWeight: 700 }}>
              {data?.cargas?.length
                ? data.cargas.map((c) => c.numeroContrato ?? c.workspaceId.slice(0, 8)).join(", ")
                : "—"}
            </Typography>
          </div>
        </Box>

        <div style={{ width: "100%", display: "flex", flexDirection: "row", gap: "16px" }}>
          {metrics.map((metric) => (
            <Metric key={metric.label} label={metric.label} value={metric.value} />
          ))}
        </div>

        <Box sx={{ marginTop: "24px" }}>
          <CargaOperadoraUploadTable data={linhas} />
        </Box>
      </>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ marginBottom: "16px" }}>
        <div style={{ display: "flex" }}>
          <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ textTransform: "none" }} />
          <Title>Carga operadora</Title>
        </div>
      </Box>

      <Typography sx={{ color: "text.secondary", fontSize: "14px", marginBottom: "24px" }}>
        A posição que a operadora informa. O upload grava a posição cadastral, que é a base contra a
        qual o Bate cadastral compara, e registra quem já está coberto para o ciclo de movimentação
        não emitir inclusão de novo. <strong>Não exporte antes da primeira aprovação.</strong>
      </Typography>

      {renderBody()}

      <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
        <Button
          variant="outlined"
          style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
          onClick={() => setIsModalOpen(true)}
        >
          Carregar planilha
        </Button>
      </Box>

      <CargaOperadoraUploadModal
        organizationId={organizationId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </Box>
  );
}
